import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Icon, Field, Avatar, DAY_SHORT } from './PrimaryUI';
import PrimaryBusFormModal from './PrimaryBusFormModal';

// ------------------------------------------------------------
const ticketBadge = (bus) => {
  const t = bus.ticketBookingType;
  if (!t) return { label: 'Not set', cls: 'gray', icon: 'info' };
  if (t.instantTicket && t.seatBooking) return { label: 'Instant + Seat', cls: 'violet', icon: 'link' };
  if (t.instantTicket) return { label: 'Instant', cls: 'green', icon: 'clock' };
  if (t.seatBooking) return { label: 'Seat booking', cls: 'amber', icon: 'seat' };
  return { label: 'None', cls: 'red', icon: 'x' };
};

// ---------- Add staff (static) ----------
const AddStaffDialog = ({ kind, onClose, onAdd }) => {
  const isDriver = kind === 'driver';
  const [f, setF] = useState({ name: '', mobileNumber: '', experience: '', licenseNumber: '' });
  const save = () => {
    if (!f.name.trim()) return toast.error('Name is required');
    if (!/^\d{10}$/.test(f.mobileNumber)) return toast.error('Enter a 10-digit mobile number');
    onAdd({
      _id: `${kind[0]}_${Date.now()}`, name: f.name.trim(), mobileNumber: f.mobileNumber, assignedBus: null,
      ...(isDriver ? { experience: Number(f.experience) || 0, licenseNumber: f.licenseNumber.trim() } : {})
    });
    toast.success(`${isDriver ? 'Driver' : 'Conductor'} added`);
    onClose();
  };
  return (
    <div className="prm-overlay prm-overlay-top" onClick={onClose}>
      <div className="prm-form-modal prm-form-modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="prm-form-head">
          <div className="prm-form-head-row">
            <span className="prm-form-badge"><Icon name={isDriver ? 'user' : 'users'} size={22} /></span>
            <div><h3>Add {kind}</h3><p>Available to assign right away</p></div>
          </div>
          <button className="prm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="prm-form-body">
          <div className="prm-fgrid">
            <Field label="Full name" required full><input className="prm-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Mobile number" required><input className="prm-input" maxLength={10} value={f.mobileNumber} onChange={(e) => setF({ ...f, mobileNumber: e.target.value.replace(/\D/g, '') })} /></Field>
            {isDriver && <Field label="Experience (years)"><input type="number" min="0" className="prm-input" value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value })} /></Field>}
            {isDriver && <Field label="License number" full><input className="prm-input" value={f.licenseNumber} onChange={(e) => setF({ ...f, licenseNumber: e.target.value })} /></Field>}
          </div>
        </div>
        <div className="prm-form-foot">
          <span />
          <div className="prm-form-foot-actions">
            <button className="prm-btn ghost" onClick={onClose}>Cancel</button>
            <button className="prm-btn primary" onClick={save}>Add {kind}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------
// MAIN — fully static (state lives in the parent page)
// ------------------------------------------------------------
const PrimaryBusManagementModal = ({
  owner, buses, drivers, conductors,
  onClose, onBusesChange, onStaffChange
}) => {
  const [tab, setTab] = useState('buses');
  const [view, setView] = useState('grid');
  const [qD, setQD] = useState('');
  const [qC, setQC] = useState('');
  const [editBus, setEditBus] = useState(null);
  const [addKind, setAddKind] = useState(null);

  const setDrivers = (next) => onStaffChange({ drivers: next, conductors });
  const setConductors = (next) => onStaffChange({ drivers, conductors: next });

  const stats = {
    buses: buses.length,
    active: buses.filter(b => b.isActive).length,
    drivers: drivers.length, aDrivers: drivers.filter(d => d.assignedBus).length,
    conductors: conductors.length, aConductors: conductors.filter(c => c.assignedBus).length
  };

  const driverOf = (id) => drivers.find(d => d.assignedBus?._id === id);
  const conductorOf = (id) => conductors.find(c => c.assignedBus?._id === id);
  const busRef = (id) => {
    const b = buses.find(x => x._id === id);
    return b ? { _id: b._id, busName: b.busName, busNumber: b.busNumber } : null;
  };

  const assign = (kind, staffId, busId) => {
    if (!staffId) return;
    const list = kind === 'driver' ? drivers : conductors;
    const next = list.map(s => s._id === staffId ? { ...s, assignedBus: busRef(busId) } : s);
    (kind === 'driver' ? setDrivers : setConductors)(next);
    toast.success(`${kind === 'driver' ? 'Driver' : 'Conductor'} assigned`);
  };
  const unassign = (kind, staffId) => {
    const list = kind === 'driver' ? drivers : conductors;
    (kind === 'driver' ? setDrivers : setConductors)(list.map(s => s._id === staffId ? { ...s, assignedBus: null } : s));
    toast.success(`${kind === 'driver' ? 'Driver' : 'Conductor'} unassigned`);
  };
  const remove = (kind, s) => {
    const msg = s.assignedBus
      ? `"${s.name}" is assigned to a bus. Unassign and delete?`
      : `Delete ${kind} "${s.name}"?`;
    if (!window.confirm(msg)) return;
    const list = kind === 'driver' ? drivers : conductors;
    (kind === 'driver' ? setDrivers : setConductors)(list.filter(x => x._id !== s._id));
    toast.success(`${kind === 'driver' ? 'Driver' : 'Conductor'} deleted`);
  };

  const saveBus = (updated) => {
    onBusesChange(buses.map(b => b._id === updated._id ? updated : b));
    // keep assigned-bus labels on staff in sync
    const sync = (list) => list.map(s => s.assignedBus?._id === updated._id
      ? { ...s, assignedBus: { _id: updated._id, busName: updated.busName, busNumber: updated.busNumber } } : s);
    onStaffChange({ drivers: sync(drivers), conductors: sync(conductors) });
    toast.success('Bus updated');
  };

  const match = (s, q) => s.name?.toLowerCase().includes(q.toLowerCase()) || s.mobileNumber?.includes(q);

  // ---------- staff column inside a bus card ----------
  const StaffSlot = ({ kind, bus }) => {
    const assigned = kind === 'driver' ? driverOf(bus._id) : conductorOf(bus._id);
    const pool = (kind === 'driver' ? drivers : conductors).filter(s => !s.assignedBus);
    return (
      <div className="pbo-slot">
        <span className="pbo-slot-ico"><Icon name={kind === 'driver' ? 'user' : 'users'} size={16} /></span>
        {assigned ? (
          <>
            <div className="pbo-slot-main">
              <b>{assigned.name}</b>
              <small><Icon name="phone" size={11} /> {assigned.mobileNumber}</small>
            </div>
            <button className="pbo-x" title="Unassign" onClick={(e) => { e.stopPropagation(); unassign(kind, assigned._id); }}>
              <Icon name="x" size={14} />
            </button>
          </>
        ) : (
          <select className="prm-input prm-select pbo-slot-select" value="" onClick={(e) => e.stopPropagation()}
            onChange={(e) => assign(kind, e.target.value, bus._id)}>
            <option value="">{pool.length ? `Assign ${kind}` : `No free ${kind}s`}</option>
            {pool.map(s => <option key={s._id} value={s._id}>{s.name} · {s.mobileNumber}</option>)}
          </select>
        )}
      </div>
    );
  };

  const DaysRow = ({ days }) => (
    <div className="prm-mini-days">{(days || []).map(d => <span key={d}>{DAY_SHORT[d]}</span>)}</div>
  );

  // ---------- staff card ----------
  const StaffCard = ({ kind, s }) => (
    <div className="pbo-staff-card">
      <div className="pbo-staff-top">
        <Avatar name={s.name} size={48} />
        <div>
          <h4>{s.name}</h4>
          <div className="pbo-meta"><Icon name="phone" size={12} /> {s.mobileNumber}</div>
        </div>
        <span className={`prm-status ${s.assignedBus ? 'active' : 'inactive'}`}>{s.assignedBus ? 'Assigned' : 'Available'}</span>
      </div>
      {kind === 'driver' && (
        <div className="pbo-chips">
          {s.experience ? <span className="prm-tag navy">{s.experience} yrs exp</span> : null}
          {s.licenseNumber ? <span className="prm-tag teal">{s.licenseNumber}</span> : null}
        </div>
      )}
      <div className={`pbo-assign-box ${s.assignedBus ? 'on' : ''}`}>
        {s.assignedBus ? (
          <>
            <Icon name="bus" size={18} />
            <div><b>{s.assignedBus.busName}</b><small>{s.assignedBus.busNumber}</small></div>
          </>
        ) : (
          <><Icon name="tick" size={16} /><span>Ready to be assigned from the Buses tab</span></>
        )}
      </div>
      <div className="pbo-staff-actions">
        {s.assignedBus && <button className="prm-btn ghost sm" onClick={() => unassign(kind, s._id)}><Icon name="refresh" size={14} /> Unassign</button>}
        <button className="prm-btn danger sm" onClick={() => remove(kind, s)}><Icon name="trash" size={14} /> Delete</button>
      </div>
    </div>
  );

  const fDrivers = drivers.filter(d => match(d, qD));
  const fConductors = conductors.filter(c => match(c, qC));

  return (
    <>
      <div className="prm-overlay" onClick={onClose}>
        <div className="prm-form-modal pbo-fleet" onClick={(e) => e.stopPropagation()}>
          {/* header */}
          <div className="prm-form-head">
            <div className="prm-form-head-row">
              <span className="prm-form-badge"><Icon name="bus" size={24} /></span>
              <div>
                <h3>Primary fleet · {owner.name}</h3>
                <p>{owner.company || 'Individual'} · weekly recurring buses</p>
              </div>
            </div>
            <button className="prm-modal-close" onClick={onClose}>×</button>
          </div>

          <div className="prm-form-body">
            {/* stats */}
            <div className="pbo-mini-stats">
              <div><span>Buses</span><b>{stats.buses}</b></div>
              <div><span>Active</span><b>{stats.active}<i>{stats.buses ? Math.round(stats.active / stats.buses * 100) : 0}%</i></b></div>
              <div><span>Drivers assigned</span><b>{stats.aDrivers}<i>/ {stats.drivers}</i></b></div>
              <div><span>Conductors assigned</span><b>{stats.aConductors}<i>/ {stats.conductors}</i></b></div>
            </div>

            {/* tabs */}
            <div className="pbo-tabbar">
              <div className="pbo-tabs" role="tablist">
                {[['buses', 'bus', 'Buses', stats.buses], ['drivers', 'user', 'Drivers', stats.drivers], ['conductors', 'users', 'Conductors', stats.conductors]].map(([k, ic, l, c]) => (
                  <button key={k} role="tab" aria-selected={tab === k} className={`pbo-tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
                    <Icon name={ic} size={15} /> {l} <span className="pbo-tab-count">{c}</span>
                  </button>
                ))}
              </div>
              {tab === 'buses' && (
                <div className="prm-layout-toggle">
                  <button className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')} title="Grid"><Icon name="grid" size={17} /></button>
                  <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')} title="List"><Icon name="list" size={17} /></button>
                </div>
              )}
              {tab === 'drivers' && <button className="prm-btn view sm-fit" onClick={() => setAddKind('driver')}><Icon name="plus" size={15} /> Add driver</button>}
              {tab === 'conductors' && <button className="prm-btn view sm-fit" onClick={() => setAddKind('conductor')}><Icon name="plus" size={15} /> Add conductor</button>}
            </div>

            {/* BUSES */}
            {tab === 'buses' && (buses.length === 0 ? (
              <div className="prm-empty"><div className="icon"><Icon name="bus" size={44} /></div><h3>No primary buses yet</h3><p>Add a bus from the owner list first.</p></div>
            ) : view === 'grid' ? (
              <div className="pbo-bus-grid">
                {buses.map(bus => {
                  const tb = ticketBadge(bus);
                  return (
                    <div key={bus._id} className={`pbo-bus-card ${bus.isActive ? '' : 'off'}`}>
                      <div className="pbo-bus-head">
                        <div>
                          <h4>{bus.busName}</h4>
                          <span className="pbo-plate">{bus.busNumber}</span>
                        </div>
                        <span className={`prm-status ${bus.isActive ? 'active' : 'inactive'}`}>{bus.isActive ? 'Active' : 'Inactive'}</span>
                      </div>
                      <div className="pbo-chips">
                        <span className="prm-tag navy">{bus.busType}</span>
                        <span className="prm-tag amber"><Icon name="star" size={11} /> Primary</span>
                        <span className={`prm-tag ${tb.cls}`}><Icon name={tb.icon} size={11} /> {tb.label}</span>
                      </div>
                      <DaysRow days={bus.departureDays} />
                      <div className="prm-metrics">
                        <div className="prm-metric"><div className="v">{bus.busCategory}</div><div className="l">Category</div></div>
                        <div className="prm-metric"><div className="v"><Icon name="seat" size={14} /> {bus.totalSeats}</div><div className="l">Seats</div></div>
                      </div>
                      <StaffSlot kind="driver" bus={bus} />
                      <StaffSlot kind="conductor" bus={bus} />
                      {bus.amenities?.length > 0 && (
                        <div className="pbo-chips">
                          {bus.amenities.slice(0, 4).map(a => <span key={a} className="pbo-amenity">{a}</span>)}
                          {bus.amenities.length > 4 && <span className="pbo-amenity">+{bus.amenities.length - 4}</span>}
                        </div>
                      )}
                      <button className="prm-btn ghost" onClick={() => setEditBus(bus)}><Icon name="edit" size={15} /> Edit bus</button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="prm-table-wrap">
                <table className="prm-table">
                  <thead><tr><th>Bus</th><th>Runs on</th><th>Seats</th><th>Driver</th><th>Conductor</th><th>Status</th><th /></tr></thead>
                  <tbody>
                    {buses.map(bus => (
                      <tr key={bus._id}>
                        <td><div className="name">{bus.busName}</div><div className="sub">{bus.busNumber} · {bus.busType}</div></td>
                        <td><DaysRow days={bus.departureDays} /></td>
                        <td>{bus.totalSeats}</td>
                        <td>{driverOf(bus._id)?.name || <span className="pbo-muted">Not assigned</span>}</td>
                        <td>{conductorOf(bus._id)?.name || <span className="pbo-muted">Not assigned</span>}</td>
                        <td><span className={`prm-status ${bus.isActive ? 'active' : 'inactive'}`}>{bus.isActive ? 'Active' : 'Inactive'}</span></td>
                        <td><button className="prm-btn ghost icon-only" onClick={() => setEditBus(bus)} title="Edit" aria-label="Edit"><Icon name="edit" size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            {/* DRIVERS */}
            {tab === 'drivers' && (
              <>
                <div className="prm-search pbo-inline-search"><Icon name="search" size={16} />
                  <input placeholder="Search drivers by name or phone…" value={qD} onChange={(e) => setQD(e.target.value)} /></div>
                {fDrivers.length ? <div className="pbo-staff-grid">{fDrivers.map(d => <StaffCard key={d._id} kind="driver" s={d} />)}</div>
                  : <div className="prm-empty"><div className="icon"><Icon name="user" size={44} /></div><h3>No drivers found</h3><p>Add a driver to start assigning buses.</p></div>}
              </>
            )}

            {/* CONDUCTORS */}
            {tab === 'conductors' && (
              <>
                <div className="prm-search pbo-inline-search"><Icon name="search" size={16} />
                  <input placeholder="Search conductors by name or phone…" value={qC} onChange={(e) => setQC(e.target.value)} /></div>
                {fConductors.length ? <div className="pbo-staff-grid">{fConductors.map(c => <StaffCard key={c._id} kind="conductor" s={c} />)}</div>
                  : <div className="prm-empty"><div className="icon"><Icon name="users" size={44} /></div><h3>No conductors found</h3><p>Add a conductor to start assigning buses.</p></div>}
              </>
            )}
          </div>

          <div className="prm-form-foot">
            <span className="prm-foot-hint">Changes are kept in memory (static data)</span>
            <div className="prm-form-foot-actions"><button className="prm-btn primary" onClick={onClose}>Done</button></div>
          </div>
        </div>
      </div>

      {editBus && <PrimaryBusFormModal owner={owner} bus={editBus} onClose={() => setEditBus(null)} onSave={saveBus} />}
      {addKind && (
        <AddStaffDialog kind={addKind} onClose={() => setAddKind(null)}
          onAdd={(s) => (addKind === 'driver' ? setDrivers([s, ...drivers]) : setConductors([s, ...conductors]))} />
      )}
    </>
  );
};

export default PrimaryBusManagementModal;